// Every image is self-hosted under /public/images. No external CDNs.
export const MODELS = {
  womanSilver: {
    id: "/images/models/woman-silver.webp",
    alt: "A silver-haired woman in a cream silk blouse, resting her hand at her collar",
    pos: "50% 22%",
  },
  womanLinen: {
    id: "/images/models/woman-linen.webp",
    alt: "A dark-haired woman in a linen shirt, standing in a quiet room",
    pos: "50% 28%",
  },
  womanYoung: {
    id: "/images/models/woman-young.webp",
    alt: "A young woman with long dark hair in a silk camisole",
    pos: "50% 28%",
  },
  manGrey: {
    id: "/images/models/man-grey.webp",
    alt: "A greying man in a pale blue shirt looking at the camera",
    pos: "50% 28%",
  },
  manBeard: {
    id: "/images/models/man-beard.webp",
    alt: "A dark-haired man with a beard in a white shirt, arms folded",
    pos: "50% 26%",
  },
};

const STOCK = {
  jettySunset: { id: "/images/stock/jetty-sunset.webp", alt: "A timber jetty reaching into the ocean beneath a golden sunset", pos: "50% 50%" },
  ceramicVase: { id: "/images/stock/ceramic-vase.webp", alt: "A white ceramic vase holding dried grasses on a quiet shelf", pos: "50% 40%" },
  linenCurtain: { id: "/images/stock/linen-curtain.webp", alt: "A sheer linen curtain moving gently beside a window in morning light" },
  norfolkPines: { id: "/images/stock/norfolk-pines.webp", alt: "Norfolk pines standing along the Cottesloe foreshore above the water", pos: "50% 30%" },
  archedAlcove: { id: "/images/stock/arched-alcove.webp", alt: "A white arched alcove with a smooth beige wall and deep soft shadows", pos: "50% 40%" },
  creamRoom: { id: "/images/stock/cream-room.webp", alt: "A serene cream room with two wall lamps and warm afternoon light", pos: "50% 50%" },
  bridalBeach: { id: "/images/stock/bridal-beach.webp", alt: "A bride and groom embracing on a sandy beach at golden hour" },
  calmSea: { id: "/images/stock/calm-sea.webp", alt: "A calm open sea under a soft sky, horizon low and quiet" },
  sittingRoom: { id: "/images/stock/sitting-room.webp", alt: "A calm, light-filled sitting room with a linen sofa and a brass lamp", pos: "50% 45%" },
  oceanDusk: { id: "/images/stock/ocean-dusk.webp", alt: "A calm ocean horizon at dusk, the water catching the last gold light", pos: "50% 45%" },
  stillSea: { id: "/images/stock/still-sea.webp", alt: "A still sea at dusk in muted tones", pos: "50% 45%" },
};

export const HAIRLINE_PLANNING = {
  id: "/images/scenes/hairline-planning.webp",
  alt: "A doctor marking a hairline plan with a white pencil in a daylit consulting room",
  pos: "45% 32%",
};

export const PHOTOS = {
  clinicReception: {
    id: "/images/lumiere-reception.webp",
    alt: "The Lumière reception — curved cream walls, timber doors and soft morning light",
    pos: "32% 35%",
    raw: true,
  },
  doctorRun: {
    id: "/images/scenes/treatment-doctor-patient.webp",
    alt: "Dr Kristy Truong in a consulting room at Lumière.",
    pos: "50% 50%",
  },
  hairNorwood: HAIRLINE_PLANNING,
  hairGrowth: STOCK.jettySunset,
  hairWomen: STOCK.ceramicVase,
  journal1: STOCK.linenCurtain,
  journal2: STOCK.norfolkPines,
  journal3: STOCK.archedAlcove,
};

export const photoSrc = (photo) => photo.id;

export const TREATMENT_PHOTOS = {
  svcInjectables: STOCK.creamRoom,
  svcThreads: {
    id: "/images/scenes/threads-regenerative.webp",
    alt: "A doctor marking guide lines along the jawline with a skin pencil in a warm treatment room",
    pos: "50% 40%",
  },
  svcSkin: MODELS.womanLinen,
  svcFacials: MODELS.womanYoung,
  svcHair: HAIRLINE_PLANNING,
  svcBody: {
    id: "/images/scenes/body-emsculpt.webp",
    alt: "A man relaxing on a treatment bed with a body-contouring applicator strapped to his abdomen",
    pos: "50% 45%",
  },
  svcMinor: {
    id: "/images/scenes/skin-assessment.webp",
    alt: "A doctor examining the skin of the cheek with a dermatoscope in a warm consulting room, an orchid behind",
    pos: "55% 40%",
  },
  svcBridal: STOCK.bridalBeach,
  svcMemberships: MODELS.womanSilver,
};

export const CONCERN_HEROES = {
  face: MODELS.womanSilver,
  skin: MODELS.womanLinen,
  hair: HAIRLINE_PLANNING,
  body: MODELS.manBeard,
  "life-stages": MODELS.womanYoung,
};

// Per-page hero overrides for concerns whose family hero doesn't fit
export const CONCERN_HERO_OVERRIDES = {
  "hair/female-thinning": {
    id: "/images/scenes/female-hair-thinning.webp",
    alt: "A woman lifting a section of her dark hair, examining its thickness in soft window light",
    pos: "50% 40%",
  },
  "life-stages/after-pregnancy": {
    id: "/images/scenes/after-pregnancy.webp",
    alt: "A new mother holding her sleeping baby against her shoulder in soft morning light",
    pos: "50% 35%",
  },
  "life-stages/mens-concerns": {
    id: "/images/scenes/mens-concerns.webp",
    alt: "A man in a white shirt standing at the water's edge at golden hour, Norfolk pines behind",
    pos: "50% 40%",
  },
};

export const PAGE_HEROES = {
  default: STOCK.calmSea,
  about: {
    id: "/images/lumiere-reception.webp",
    alt: "The Lumière reception — curved cream walls, timber doors and soft morning light",
    pos: "50% 30%",
    raw: true,
  },
  team: {
    id: "/images/lumiere-reception.webp",
    alt: "The Lumière reception — curved cream walls, caramel chairs and an orchid in soft daylight",
    pos: "50% 62%",
    raw: true,
    className: "scale-[1.6] origin-bottom lg:scale-100",
  },
  firstVisit: MODELS.womanLinen,
  treatments: {
    id: "/images/scenes/treatment-doctor-patient.webp",
    alt: "Dr Kristy Truong beside a treatment chair in a warm, light-filled room",
    pos: "50% 50%",
  },
  concerns: {
    id: "/images/models/woman-mirror.webp",
    alt: "A woman studying her skin in a round mirror in soft window light",
    pos: "50% 30%",
  },
  contact: STOCK.oceanDusk,
  journal: STOCK.stillSea,
  book: {
    id: "/images/lumiere-reception.webp",
    alt: "Inside the Lumière clinic — the reception lounge in warm cream and timber",
    pos: "50% 30%",
    raw: true,
  },
};
